import React from 'react';
import PropTypes from 'prop-types';
import { connect, Field } from 'formik';

import 'checkbox_group.scss';
import FormGroup from './form_group';
import FormTooltip, { tooltipPropShape, tooltipDefaultProp } from './form_tooltip';

const genId = (name, value) => `${name}-${value}`.replace(/\./g, '-');

export const CheckboxInput = ({ checked, fieldValue, form, id, name, onChange, value }) => {
  return (
    <input
      type="checkbox"
      id={id}
      name={name}
      checked={checked}
      onChange={() => {
        let nextFieldValue;

        if (checked) {
          nextFieldValue = fieldValue.filter(currentValue => currentValue !== value);
          form.setFieldValue(name, nextFieldValue);
        } else {
          nextFieldValue = [...fieldValue, value];
          form.setFieldValue(name, nextFieldValue);
        }

        onChange(name, nextFieldValue);
      }}
    />
  );
};

CheckboxInput.propTypes = {
  checked: PropTypes.bool.isRequired,
  fieldValue: PropTypes.arrayOf(PropTypes.string).isRequired,
  form: PropTypes.shape({ setFieldValue: PropTypes.func }).isRequired,
  id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  value: PropTypes.string.isRequired,
};

export const FormCheckbox = ({ formik, label, name, onChange, tooltip, value }) => {
  const checkboxId = genId(name, value);
  const { content, position, header } = tooltip;

  const fieldValue = formik.values[name] || [];
  const checked = fieldValue.includes(value);

  return (
    <div className="react-checkbox">
      <FormTooltip content={content} position={position} header={header}>
        <label htmlFor={checkboxId} className={checked ? 'selected' : undefined}>
          <Field
            name={name}
            render={({ form }) => (
              <CheckboxInput
                checked={checked}
                fieldValue={fieldValue}
                form={form}
                id={checkboxId}
                name={name}
                onChange={onChange}
                value={value}
              />
            )}
          />
          {label}
        </label>
      </FormTooltip>
    </div>
  );
};

FormCheckbox.defaultProps = {
  tooltip: tooltipDefaultProp,
};

FormCheckbox.propTypes = {
  formik: PropTypes.shape({ values: PropTypes.object }).isRequired,
  label: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  tooltip: tooltipPropShape,
  value: PropTypes.string.isRequired,
};

const CheckboxGroup = ({
  formGroupClassName,
  formGroupLabel,
  formGroupSubtitle,
  formik,
  name,
  onChange,
  options,
  radioGroupClassName,
  tooltip,
}) => (
  <FormGroup className={formGroupClassName} label={formGroupLabel} subtitle={formGroupSubtitle}>
    <div className={radioGroupClassName}>
      {options.map(({ label, value }) => (
        <FormCheckbox
          key={label}
          label={label}
          name={name}
          onChange={onChange}
          tooltip={tooltip}
          value={value}
          formik={formik}
        />
      ))}
    </div>
  </FormGroup>
);

CheckboxGroup.propTypes = {
  formGroupClassName: PropTypes.string,
  formGroupLabel: PropTypes.string.isRequired,
  formGroupSubtitle: PropTypes.string,
  formik: PropTypes.shape({ values: PropTypes.object }),
  name: PropTypes.string.isRequired,
  onChange: PropTypes.func,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
    })
  ).isRequired,
  radioGroupClassName: PropTypes.string,
  tooltip: tooltipPropShape,
};

CheckboxGroup.defaultProps = {
  formGroupClassName: null,
  formGroupSubtitle: null,
  formik: { values: {} },
  onChange: () => {},
  radioGroupClassName: null,
  tooltip: tooltipDefaultProp,
};

export default connect(CheckboxGroup);
